import './InfoPopupStyle.css';
import { useState, useEffect, useRef } from "react";

/**
 * Singleton component for displaying a popup.
 * The entire application only has one instance of it.
 * Listens for a "showInfoPopup" custom event to display content.
 */
export function InfoPopup() 
{
    const [isOpen, setIsOpen] = useState(false); //Tracks whether the popup is visible
    const [content, setContent] = useState(null as React.ReactNode); //Holds the popup content
    const popupRef = useRef<HTMLDivElement | null>(null); //Reference to the popup DOM element

    //Set up event listeners for opening/closing the popup
    useEffect(function setupEventListeners() 
    {
        /**
         * Handles the "showInfoPopup" custom event by setting the popup content and opening it.
         */
        function handleShowPopup(event: CustomEvent<React.ReactNode>)
        {
            setContent(event.detail);
            setIsOpen(true);
        }

        /**
         * Closes the popup when the Escape key is pressed.
         */
        function handleKeyDown(event: KeyboardEvent)
        {
            if (event.key === "Escape") 
            {
                setIsOpen(false);
            }
        }

        /**
         * Closes the popup if the user clicks outside of it.
         */
        function handleClickOutside(event: MouseEvent)
        {
            if (!popupRef.current?.contains(event.target as Node)) 
            {
                setIsOpen(false);
            }
        }

        //Attach event listeners
        document.addEventListener("showInfoPopup", handleShowPopup as EventListener);
        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("mousedown", handleClickOutside);
    }, []);

    //If the popup is not open, render nothing
    if (!isOpen) 
    {
        return null;
    }

    //Render the popup and a semi-transparent overlay
    return (
        <>
            {/* Overlay that closes the popup when clicked */}
            <div className='ernots_InfoPopupOverlay'/>

            {/* Centered popup with content */}
            <div className='ernots_InfoPopup' ref={popupRef}>
                {content}
            </div>
        </>
    );
}